import { ArrowForwardOutlined, KeyboardOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";

import { OTHER_LABEL } from "config/LabelMapping";
import { useReviewPageHotkeys } from "hooks/useHotkeys/useHotkeys";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { Posicheck } from "types/imageQueries";

import { UnclearActionsRow } from "./UnclearActionsRow";

export interface MultichoiceImageActionsProps {
  imageQueryId?: Posicheck["id"];
  isUnclearMenuOpen: boolean;
  setUnclearMenuOpen: (isOpen: boolean) => void;
  onUnclearSubmit: (feedbackText: string) => void;
  onLabelSubmit: (value: string) => void;
  choices: string[];
  showDeleteButton?: boolean;
  onDeleteImageQuery?: () => void | Promise<void>;
  isDeletingImageQuery?: boolean;
}

export const MAX_CHOICE_BUTTONS = 6;

export function MultichoiceImageActions(props: MultichoiceImageActionsProps) {
  const {
    imageQueryId,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    onUnclearSubmit,
    onLabelSubmit,
    choices,
    showDeleteButton,
    onDeleteImageQuery,
    isDeletingImageQuery,
  } = props;

  const [selectedValue, setSelectedValue] = useState<string>("");
  const [valueError, setValueError] = useState<string>("");
  const isXS = useMediaQuery(useTheme().breakpoints.down("sm"));
  const isSM = useMediaQuery(useTheme().breakpoints.down("md"));

  const hasImageQuery = Boolean(imageQueryId);
  const displayableChoices = choices.filter((choice: string) => choice !== OTHER_LABEL);

  const { useUnclearHotkey, useMultichoiceHotkeys, useOtherHotkey } = useReviewPageHotkeys({
    detectorMode: DetectorModes.MULTICHOICE,
    isUnclearMenuOpen,
    setUnclearMenuOpen,
    hasImageQuery,
    onLabelSubmit,
    choices,
  });

  const handleChange = (event: SelectChangeEvent) => {
    setSelectedValue(event.target.value);
    setValueError("");
  };

  const handleSubmit = () => {
    if (selectedValue === "") {
      setValueError("The value is required");
      return;
    }

    if (valueError) return;

    onLabelSubmit(displayableChoices[Number(selectedValue)]);
  };

  const handleOtherSubmit = () => {
    onLabelSubmit(OTHER_LABEL);
  };

  useUnclearHotkey();
  useMultichoiceHotkeys();
  useOtherHotkey();

  return (
    <Stack
      id="multichoice-actions"
      sx={{
        flexDirection: { xs: "column", sm: "row" },
        alignItems: "center",
        gap: "20px",
        backgroundColor: "#E5E9ED",
        borderRadius: "22px",
        padding: "22px",
      }}
    >
      <Box
        id="multichoice-actions-options"
        sx={{
          flexGrow: { xs: 0, sm: 1 },
          height: { xs: "auto", sm: "100%" },
          width: { xs: "100%", sm: "auto" },
        }}
      >
        {displayableChoices.length > 0 && displayableChoices.length <= MAX_CHOICE_BUTTONS ? (
          <Grid container spacing="10px">
            {displayableChoices.map((choice, index) => (
              <Grid item xs={displayableChoices.length > 3 ? 4 : 12 / displayableChoices.length} key={choice}>
                <Tooltip title={choice} placement={index > 2 ? "bottom" : "top"}>
                  <Button
                    onClick={() => onLabelSubmit(choice)}
                    variant="contained"
                    sx={{
                      id: `multichoice-actions-button-${index}`,
                      width: "100%",
                      height: "34px",
                      bgcolor: "#529FE8",
                      color: "#FFFFFF",
                      justifyContent: "center",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      borderRadius: "17px",
                      "& .MuiButton-endIcon": {
                        color: "FFFFFF",
                      },
                      "&:hover": {
                        bgcolor: "primary.light",
                        color: "common.white",
                        "& .MuiButton-endIcon": {
                          color: "common.white",
                        },
                      },
                    }}
                    endIcon={
                      <Box className="items-center opacity-60 h-full hidden md:flex gap-1">
                        <KeyboardOutlined sx={{ height: "24px", width: "24px" }} />
                        <Typography variant="small" fontWeight="bold">
                          {index + 1}
                        </Typography>
                      </Box>
                    }
                  >
                    <Typography noWrap>{choice}</Typography>
                  </Button>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <Box sx={{ flex: 1, backgroundColor: "white" }}>
              <Select
                id="multichoice-actions-select"
                value={selectedValue}
                onChange={handleChange}
                fullWidth
                displayEmpty
                renderValue={(value: string) => {
                  if (value === "") {
                    return <Typography color="text.disabled">Select an option</Typography>;
                  }
                  return displayableChoices[Number(value)];
                }}
                sx={{ flex: 1, height: "37px" }}
                error={!!valueError}
                MenuProps={{ sx: { maxHeight: "2500px" } }}
              >
                {displayableChoices.map((choice, index) => (
                  <MenuItem key={`option-${choice}`} value={index}>
                    <Tooltip title={choice} placement="left">
                      <Typography textTransform="none" noWrap>
                        {choice}
                      </Typography>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Button
              variant="contained"
              color="primary"
              sx={{
                display: { xs: "none", sm: "block" },
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: "400",
                padding: "3px 20px",
                borderRadius: "17px",
                textTransform: "none",
              }}
              onClick={handleSubmit}
              disabled={selectedValue === ""}
            >
              Submit
            </Button>
            <IconButton
              sx={{
                display: { sm: "none" },
                padding: "3px",
                backgroundColor: "#006DD9",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "#006DD9" },
              }}
              onClick={handleSubmit}
              disabled={selectedValue === ""}
            >
              <ArrowForwardOutlined sx={{ height: "24px", width: "24px" }} />
            </IconButton>
          </Box>
        )}
      </Box>
      <Divider
        id="multichoice-actions-divider"
        orientation={isXS ? "horizontal" : "vertical"}
        sx={{
          alignSelf: { xs: "center", sm: "auto" },
          width: { xs: "90%", sm: "auto" },
          height: { xs: "auto", sm: "86px" },
          borderColor: "#000000",
        }}
      />
      <Box
        id="multichoice-actions-unclear"
        sx={{
          display: "flex",
          flexDirection: { xs: "row", sm: "column" },
          gap: { xs: "5px", sm: "10px" },
          width: { xs: "100%", sm: "auto" },
          flexShrink: 0,
        }}
      >
        <UnclearActionsRow
          hasImageQuery={hasImageQuery}
          isUnclearMenuOpen={isUnclearMenuOpen}
          setUnclearMenuOpen={setUnclearMenuOpen}
          onUnclearSubmit={onUnclearSubmit}
          showDeleteButton={showDeleteButton}
          onDeleteImageQuery={onDeleteImageQuery}
          isDeletingImageQuery={isDeletingImageQuery}
        />
        {choices.length > 0 && choices.includes(OTHER_LABEL) && (
          <Button
            id="multichoice-actions-other-button"
            fullWidth
            onClick={handleOtherSubmit}
            variant="outlined"
            sx={{
              height: "34px",
              bgcolor: "#FFFFFF",
              borderRadius: "17px",
              textTransform: "none",
              justifyContent: "space-around",
              color: "#0450BA",
              fontSize: "20px",
              padding: "0px 18px",
              border: "1px solid #0450BA",

              "& .MuiButton-endIcon": {
                margin: "0",
              },
            }}
            endIcon={
              !isSM && (
                <Box
                  sx={{
                    display: { xs: "none", md: "flex" },
                    height: "100%",
                    width: "36px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    color: "#0450BA",
                    opacity: 0.6,
                    lineHeight: "14px",
                    fontWeight: "900",
                    fontSize: "12px",
                  }}
                >
                  <KeyboardOutlined
                    sx={{
                      height: "24px",
                      width: "24px",
                    }}
                  />
                  <Typography variant="small" fontWeight="bold">
                    O
                  </Typography>
                </Box>
              )
            }
          >
            Other
          </Button>
        )}
      </Box>
    </Stack>
  );
}
